import React, { useEffect, useRef, useState } from "react";
import { AnimatedTick, Button, Receipt, ReceiptStoreCode, SidePaneContent, Toast } from "../../../../../../Components";
import { FiCheckCircle, FiSmartphone, FiX } from "react-icons/fi";
import { RiDragDropLine } from "react-icons/ri";
import { useClientSettings } from "../../../../../../Hooks/useClientSettings";
import useMobileCheck from "../../../../../../Hooks/useMobileCheck";
import { useUserPreference } from "../../../../../../Hooks/useUserPreference";
import ReceiptDrop from "./drop";
import { apis, get, post } from "../../../../../../Utils/axios";
import { getReceiptReimbursementEligibility } from "../../../../../../Utils/expenseReimbursement";

function SubmitReceipt({ dragging, quickAddReceipt, loading, forceCloseSignal = 0, onReload = () => {} }) {
	const { isEnabled, currency } = useClientSettings();
	const { showSidePane, sidePane } = useUserPreference();
	const { isMobile } = useMobileCheck();
	const expenseEnabled = isEnabled("ORGANIZATION") && isEnabled("EXPENSE");

	const [Success, setSuccess] = useState(false);
	const [Loading, setLoading] = useState(false);
	const [SelectedReceipts, setSelectedReceipts] = useState([]);
	const [StoreUploadUrl, setStoreUploadUrl] = useState("");
	const [Dismissed, setDismissed] = useState(false);
	const quickAddProcessedRef = useRef(null);
	const processedForceCloseRef = useRef(0);
	const totalAmount = SelectedReceipts.reduce((acc, receipt) => acc + Number(receipt.amount), 0);
	const closePane = () => {
		setDismissed(true);
		showSidePane(false);
	};

	const openMobileUploader = () => {
		if (StoreUploadUrl) {
			if (isMobile) {
				window.location.href = StoreUploadUrl;
			} else {
				window.open(StoreUploadUrl, "_blank", "noopener,noreferrer");
			}
		} else {
			Toast.error("Upload link is still preparing. Please try again.");
		}
	};

	useEffect(() => {
		setSelectedReceipts([]);
	}, [loading]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const uploadedFlag = params.get("receiptUploaded");
		const uploadedCount = Number(uploadedFlag);
		const shouldClosePane = uploadedFlag === "true" || (Number.isFinite(uploadedCount) && uploadedCount >= 1);
		if (shouldClosePane && sidePane) {
			setDismissed(true);
			showSidePane(false);
			Toast.success("Files uploaded. You can continue from receipts list.");
			params.delete("receiptUploaded");
			params.delete("openAddReceipt");
			const query = params.toString();
			window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
		}
	}, [sidePane]);

	useEffect(() => {
		// If user explicitly opens the side pane again, allow this content to render.
		if (sidePane && Dismissed) {
			setDismissed(false);
		}
	}, [sidePane, Dismissed]);

	useEffect(() => {
		if (forceCloseSignal > processedForceCloseRef.current) {
			processedForceCloseRef.current = forceCloseSignal;
			setDismissed(true);
			showSidePane(false);
		}
	}, [forceCloseSignal]);

	useEffect(() => {
		const buildStoreUrl = (code) => {
			const returnTo = encodeURIComponent(`${window.location.pathname}${window.location.search || ""}`);
			return `${window.location.origin}/store?code=${code}&returnTo=${returnTo}`;
		};
		let cached = {};
		try {
			cached = JSON.parse(localStorage.getItem("receiptStore") || "{}");
		} catch (e) {
			cached = {};
		}

		if (cached?.code) {
			setStoreUploadUrl(buildStoreUrl(cached.code));
			return;
		}

		get(apis.getStoreCode, {}, (r) => {
			if (r?.code) {
				localStorage.setItem("receiptStore", JSON.stringify(r));
				setStoreUploadUrl(buildStoreUrl(r.code));
			}
		});
	}, []);

	const onReceiptDrop = () => {
		if (!dragging?.id) return;
		const { canAddToReimbursement, toastReason, disabledReason } = getReceiptReimbursementEligibility(dragging);
		if (!canAddToReimbursement) {
			if (toastReason || disabledReason) Toast.error(toastReason || disabledReason);
			return;
		}
		const updatedSelection = SelectedReceipts.filter((receipt) => receipt.id !== dragging.id);
		setSelectedReceipts([...updatedSelection, dragging]);
	};

	useEffect(() => {
		const addReceiptToSelection = (receipt = {}) => {
			if (!receipt?.id) return;
			const { canAddToReimbursement, toastReason, disabledReason } = getReceiptReimbursementEligibility(receipt);
			if (!canAddToReimbursement) {
				if (toastReason || disabledReason) Toast.error(toastReason || disabledReason);
				return;
			}
			setSelectedReceipts((prev) => {
				const existing = prev.some((r) => r.id === receipt.id);
				if (existing) return prev;
				return [...prev, receipt];
			});
			showSidePane(true);
		};

		if (quickAddReceipt?.id) {
			const quickAddKey = quickAddReceipt.__quickAddKey || quickAddReceipt.id;
			if (quickAddProcessedRef.current === quickAddKey) {
				return;
			}
			quickAddProcessedRef.current = quickAddKey;
			addReceiptToSelection(quickAddReceipt);
		}
	}, [quickAddReceipt, showSidePane]);

	if (!expenseEnabled) {
		//If expense is not enabled do not show reimbursement side pane
		return null;
	}

	if (Dismissed) {
		return null;
	}

	const removeSelection = (id) => {
		setSelectedReceipts(SelectedReceipts.filter((receipt) => receipt.id !== id));
	};

	const onSubmit = () => {
		setLoading(true);
		post(apis.submitReceipts, { receipts: SelectedReceipts.map((r) => r.id) }, (r, e) => {
			if (r) {
				setSelectedReceipts([]);
				setLoading(false);
				setSuccess(true);
				setTimeout(() => {
					setSuccess(false);
				}, 3000);
				onReload();
			} else if (e) {
				setLoading(false);
				Toast.handleError(e);
			}
		});
	};

	if (SelectedReceipts.length) {
		return (
			<SidePaneContent>
				<div className={`w-full h-full flex flex-col justify-between bg-light relative`}>
					<div onDragOver={(e) => e.preventDefault()} onDrop={onReceiptDrop} className="flex-grow w-full overflow-y-auto">
						<div className="sticky top-0 p-3 border-b bg-light">
							<div className="flex items-start justify-between gap-2">
								<div>
									<h4>{SelectedReceipts.length} Receipt{SelectedReceipts.length > 1 ? "s" : ""} Ready</h4>
									<div className="mt-1 text-xs text-secondary">Review and submit for reimbursement</div>
								</div>
								<Button onClick={closePane} variant="light" className="btn-xs !px-2.5 gap-1">
									<FiX /> Close
								</Button>
							</div>
						</div>
						<div className="flex flex-col gap-2 p-2">
							{SelectedReceipts.map((receipt, i) => {
								return <Receipt key={i} {...receipt} onDelete={() => removeSelection(receipt.id)} />;
							})}
						</div>
					</div>
					<div className="flex flex-col gap-2 p-2">
						<div className="p-2 text-white rounded-lg bg-dark">
							<h1 className="mb-2 text-xl">
								Total {currency} {totalAmount}
							</h1>
							<Button loading={Loading} onClick={onSubmit} variant="primary" className="w-full">
								Send for Reimbursement
							</Button>
						</div>
					</div>
					<ReceiptDrop dragging={dragging} onDrop={onReceiptDrop} />
				</div>
			</SidePaneContent>
		);
	}

	return (
		<>
			<SidePaneContent>
				<div className="relative flex flex-col w-full h-full gap-2 p-3">
					<div className="flex justify-end">
						<Button onClick={closePane} variant="light" className="btn-xs !px-2.5 gap-1">
							<FiX /> Close
						</Button>
					</div>
					<div onDragOver={(e) => e.preventDefault()} onDrop={onReceiptDrop} className="flex-grow w-full">
						{Success ? (
							<div className="w-full h-full flex-center">
								<div>
									<AnimatedTick />
									<h3 className="mt-6">Receipts Submitted</h3>
								</div>
							</div>
						) : (
							<div className="h-full p-4 border border-dashed rounded-2xl bg-canvas/70 border-surface">
								<div className="text-center">
									<RiDragDropLine size={44} className="m-auto mb-4 text-secondary" />
									<h4 className="mb-2">Submit Receipts for Reimbursement</h4>
									<div className="text-xs text-secondary">{isMobile ? "Use your phone to upload image or PDF receipts" : "Drag eligible receipts here to add them for reimbursement"}</div>
								</div>
								<div className="mt-4 space-y-2 text-xs">
									<div className="p-2 rounded-lg bg-light flex items-start gap-2">
										<FiCheckCircle className="mt-0.5 text-successText" />
										<span>Upload receipt image/PDF and set category.</span>
									</div>
									<div className="p-2 rounded-lg bg-light flex items-start gap-2">
										<FiCheckCircle className="mt-0.5 text-successText" />
										<span>Only in-policy or approval-needed receipts can be submitted.</span>
									</div>
									<div className="p-2 rounded-lg bg-light flex items-start gap-2">
										<FiCheckCircle className="mt-0.5 text-successText" />
										<span>After adding receipts here, click Send for Reimbursement.</span>
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="flex flex-col gap-2">
						<div className="p-3 text-xs text-center border rounded-xl bg-light border-surface">
							<Button onClick={openMobileUploader} variant="primary" className="w-full mb-3 btn-sm gap-2">
								<FiSmartphone /> Upload image/PDF from phone
							</Button>
							<ReceiptStoreCode className="m-auto mb-3" />
							<div className="text-secondary">Scan QR if uploading from another device</div>
						</div>
					</div>
					<ReceiptDrop dragging={dragging} onDrop={onReceiptDrop} />
				</div>
			</SidePaneContent>
		</>
	);
}

export default SubmitReceipt;
